import { useState, useRef, useEffect } from "react";

const T = {
  bg:"#0B0F1A", surface:"#111827", card:"#1A2235", border:"#1E2D45",
  accent:"#38BDF8", green:"#34D399", yellow:"#FCD34D", red:"#F87171",
  purple:"#A78BFA", orange:"#FB923C", textPri:"#F0F6FF", textSec:"#94A3B8",
  textMute:"#4B5563", codeBg:"#060D18", codeStr:"#86EFAC", codeKw:"#7DD3FC",
  codeFn:"#FDE68A", codeCmt:"#475569", codeNum:"#F9A8D4",
};

function highlight(code) {
  const kws = ["import","def","while","if","else","return","print","from","as","class","True","False","None","and","or","not","in","for","with"];
  const fns = ["Thread","Process","start","join","sleep","Queue","target"];
  return code.split("\n").map((line, li) => {
    if (line.trimStart().startsWith("#")) {
      return <div key={li} style={{minHeight:"1.4em",color:T.codeCmt}}>{line}</div>;
    }
    const tokens = [];
    let i = 0;
    while (i < line.length) {
      if (line[i]==="#") {
        tokens.push(<span key={i} style={{color:T.codeCmt}}>{line.slice(i)}</span>);
        i = line.length; continue;
      }
      if (line[i]==="'" || line[i]==='"') {
        const q=line[i]; let j=i+1;
        while(j<line.length && line[j]!==q) j++;
        tokens.push(<span key={i} style={{color:T.codeStr}}>{line.slice(i,j+1)}</span>);
        i=j+1; continue;
      }
      if (/\d/.test(line[i]) && (i===0||!/\w/.test(line[i-1]))) {
        let j=i; while(j<line.length && /[\d_.]/.test(line[j])) j++;
        tokens.push(<span key={i} style={{color:T.codeNum}}>{line.slice(i,j)}</span>);
        i=j; continue;
      }
      if (/[a-zA-Z_]/.test(line[i])) {
        let j=i; while(j<line.length && /\w/.test(line[j])) j++;
        const word=line.slice(i,j);
        let color=T.textPri;
        if (kws.includes(word)) color=T.codeKw;
        else if (fns.includes(word)) color=T.codeFn;
        tokens.push(<span key={i} style={{color}}>{word}</span>);
        i=j; continue;
      }
      tokens.push(<span key={i} style={{color:T.textSec}}>{line[i]}</span>);
      i++;
    }
    return <div key={li} style={{minHeight:"1.4em"}}>{tokens}</div>;
  });
}

function CodeBlock({ code, label="python" }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{marginBottom:16,borderRadius:10,overflow:"hidden",border:`1px solid ${T.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
        background:"#0D1929",padding:"6px 14px",borderBottom:`1px solid ${T.border}`}}>
        <span style={{fontSize:11,color:T.accent,fontFamily:"monospace",letterSpacing:.5}}>{label}</span>
        <button onClick={()=>{navigator.clipboard.writeText(code);setCopied(true);setTimeout(()=>setCopied(false),1500);}}
          style={{fontSize:11,color:copied?T.green:T.textSec,background:"none",border:"none",cursor:"pointer",padding:"2px 8px",borderRadius:4}}>
          {copied?"✓ copied":"copy"}
        </button>
      </div>
      <div style={{background:T.codeBg,padding:"14px 16px",overflowX:"auto",
        fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.65}}>
        {highlight(code)}
      </div>
    </div>
  );
}

function Output({ children }) {
  return (
    <div style={{background:"#060D18",border:`1px solid ${T.green}44`,borderTop:"none",
      borderRadius:"0 0 10px 10px",padding:"12px 16px",marginBottom:16,
      fontFamily:"'JetBrains Mono','Fira Mono',monospace",fontSize:12.5,lineHeight:1.7,
      color:T.green}}>
      {children}
    </div>
  );
}

function Callout({ type="default", children }) {
  const styles = {
    default: { bg:"#1a0f06", border:T.orange,    icon:"💡" },
    question: { bg:"#071929", border:T.accent,   icon:"❓" },
    win:     { bg:"#0a1a10", border:T.green,     icon:"✅" },
  };
  const s = styles[type] || styles.default;
  return (
    <div style={{background:s.bg,borderLeft:`3px solid ${s.border}`,borderRadius:"0 8px 8px 0",
      padding:"14px 18px",marginBottom:18,fontSize:13.5,color:T.textSec,lineHeight:1.7}}>
      {children}
    </div>
  );
}

function TTable({ headers, rows }) {
  return (
    <div style={{overflowX:"auto",marginBottom:16}}>
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
        <thead>
          <tr>{headers.map((h,i)=>(
            <th key={i} style={{background:"#0D1929",color:T.accent,padding:"8px 14px",
              borderBottom:`1px solid ${T.border}`,textAlign:"left",fontWeight:600,
              fontSize:11,letterSpacing:.5,textTransform:"uppercase"}}>
              {h}
            </th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.map((row,ri)=>(
            <tr key={ri} style={{background:ri%2===0?T.card:T.surface}}>
              {row.map((cell,ci)=>(
                <td key={ci} style={{padding:"10px 14px",color:ci===0?T.textPri:T.textSec,
                  borderBottom:`1px solid ${T.border}33`,lineHeight:1.6,
                  fontWeight:ci===0?600:400}}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── INTERACTIVE DEMO ──────────────────────────────────────────────────────────
const DUR = 3000;

function BreakfastDemo() {
  const [teaPct, setTeaPct]     = useState(0);
  const [toastPct, setToastPct] = useState(0);
  const [teaLabel, setTeaLabel] = useState("");
  const [toastLabel, setToastLabel] = useState("");
  const [elapsed, setElapsed]   = useState(0);
  const [verdict, setVerdict]   = useState("");
  const [running, setRunning]   = useState(false);
  const timerRef = useRef(null);

  function reset() {
    clearInterval(timerRef.current);
    setTeaPct(0); setToastPct(0);
    setTeaLabel(""); setToastLabel("");
    setElapsed(0); setVerdict("");
    setRunning(false);
  }

  function run(mode) {
    reset();
    setRunning(true);
    const t0 = performance.now();
    const total = mode === "seq" ? DUR * 2 : DUR;
    timerRef.current = setInterval(() => {
      const el = performance.now() - t0;
      setElapsed(Math.min(el, total));
      const teaP   = Math.min(el / DUR, 1);
      const toastP = mode === "seq"
        ? Math.max(0, Math.min((el - DUR) / DUR, 1))
        : Math.min(el / DUR, 1);
      setTeaPct(teaP * 100);
      setToastPct(toastP * 100);
      setTeaLabel(teaP >= 1 ? "READY! ✅" : teaP > 0 ? "brewing…" : "");
      setToastLabel(toastP >= 1 ? "READY! ✅" : toastP > 0 ? "toasting…"
        : mode === "seq" ? "waiting for tea… 😴" : "");
      if (el >= total) {
        clearInterval(timerRef.current);
        setRunning(false);
        setVerdict(mode === "seq"
          ? "Breakfast took 6 seconds. Toaster sat idle the whole first half! 😴"
          : "Breakfast in 3 seconds — both chores overlapped. That's threading! ⚡");
      }
    }, 50);
  }

  useEffect(() => () => clearInterval(timerRef.current), []);

  return (
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,
      padding:24,marginBottom:20,fontFamily:"system-ui,sans-serif"}}>
      <div style={{fontSize:14,fontWeight:600,color:T.textPri,marginBottom:4}}>🍳 Try it yourself</div>
      <div style={{fontSize:12.5,color:T.textMute,marginBottom:18}}>Same two chores. Watch the clock.</div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
        <button onClick={()=>run("seq")} disabled={running}
          style={{background:T.surface,border:`1px solid ${T.border}`,color:T.textPri,
            fontFamily:"inherit",fontSize:13,padding:"9px 16px",borderRadius:8,cursor:running?"not-allowed":"pointer",
            opacity:running?.5:1}}>
          🤖 Robot: one at a time
        </button>
        <button onClick={()=>run("thr")} disabled={running}
          style={{background:"rgba(56,189,248,0.1)",border:`1px solid ${T.accent}44`,color:T.accent,
            fontFamily:"inherit",fontSize:13,padding:"9px 16px",borderRadius:8,cursor:running?"not-allowed":"pointer",
            opacity:running?.5:1,fontWeight:600}}>
          ⚡ You: start both, then wait
        </button>
        <button onClick={reset} disabled={running}
          style={{background:"none",border:`1px solid ${T.border}`,color:T.textMute,
            fontFamily:"inherit",fontSize:13,padding:"9px 16px",borderRadius:8,cursor:running?"not-allowed":"pointer",
            opacity:running?.5:1}}>
          ↺ Reset
        </button>
      </div>
      {/* Tea bar */}
      <div style={{marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,color:T.textSec,marginBottom:5}}>
          <span>🫖 Tea</span>
          <span style={{color:teaPct>=100?T.green:T.textMute}}>{teaLabel}</span>
        </div>
        <div style={{height:24,background:T.surface,borderRadius:6,overflow:"hidden",border:`1px solid ${T.border}`}}>
          <div style={{height:"100%",width:`${teaPct}%`,background:"#8a6f3c",
            borderRadius:6,transition:"width .08s linear",display:"flex",alignItems:"center",
            paddingLeft:8,fontSize:11,color:"#fff",whiteSpace:"nowrap"}}>
            {teaPct>15 ? "brewing" : ""}
          </div>
        </div>
      </div>
      {/* Toast bar */}
      <div style={{marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:12.5,color:T.textSec,marginBottom:5}}>
          <span>🍞 Toast</span>
          <span style={{color:toastPct>=100?T.green:T.textMute}}>{toastLabel}</span>
        </div>
        <div style={{height:24,background:T.surface,borderRadius:6,overflow:"hidden",border:`1px solid ${T.border}`}}>
          <div style={{height:"100%",width:`${toastPct}%`,background:T.orange,
            borderRadius:6,transition:"width .08s linear",display:"flex",alignItems:"center",
            paddingLeft:8,fontSize:11,color:"#fff",whiteSpace:"nowrap"}}>
            {toastPct>15 ? "toasting" : ""}
          </div>
        </div>
      </div>
      <div style={{fontSize:28,fontFamily:"monospace",textAlign:"center",color:T.accent,fontWeight:600,letterSpacing:2}}>
        {(elapsed / 1000).toFixed(1)}s
      </div>
      {verdict && (
        <div style={{marginTop:10,textAlign:"center",fontSize:13,
          color:verdict.includes("6 seconds")?T.red:T.green,fontWeight:600,lineHeight:1.5}}>
          {verdict}
        </div>
      )}
    </div>
  );
}

// ── SECTION COMPONENTS ────────────────────────────────────────────────────────

function S1() { return (
  <div>
    <p style={{fontSize:15,color:T.textSec,lineHeight:1.8,marginBottom:16}}>
      Tea needs 3 minutes (waiting for water to boil). Toast needs 3 minutes (waiting for the toaster).
      A very literal-minded robot does one chore <em style={{color:T.accent}}>completely</em> before starting the next:
    </p>
    <CodeBlock code={`import time

def make_tea():
    print("tea: put water to boil...")
    time.sleep(3)                     # waiting for water
    print("tea: READY!")

def make_toast():
    print("toast: bread in toaster...")
    time.sleep(3)                     # waiting for toaster
    print("toast: READY!")

make_tea()
make_toast()`} />
    <Output>
      <div>tea: put water to boil...</div>
      <div>tea: READY!</div>
      <div>toast: bread in toaster...</div>
      <div>toast: READY!</div>
      <div style={{color:T.textMute,marginTop:4}}>breakfast took 6 seconds</div>
    </Output>
    <Callout type="question">
      <strong style={{color:T.accent}}>Ask yourself:</strong> is that how <em>you</em> make breakfast?
      Do you stare at the kettle for 3 minutes before you're allowed to touch the toaster?
      Of course not. You start both — <em>then</em> you wait.
    </Callout>
  </div>
);}

function S2() { return (
  <div>
    <p style={{fontSize:15,color:T.textSec,lineHeight:1.8,marginBottom:16}}>
      That everyday instinct — <em style={{color:T.accent}}>start both chores, then wait for both</em> — is exactly what a thread is.
      Only four new words to learn:
    </p>
    <CodeBlock code={`import threading

tea   = threading.Thread(target=make_tea)     # hire a helper, hand them the recipe
toast = threading.Thread(target=make_toast)   # (no parentheses — don't cook it yourself!)

tea.start()      # "go!" — and don't wait
toast.start()    # "go!" — and don't wait

tea.join()       # NOW stand here until tea is done
toast.join()     # and until toast is done`} />
    <Output>
      <div>tea: put water to boil...</div>
      <div>toast: bread in toaster...
        <span style={{color:T.textMute,marginLeft:12}}>&lt;- toast began BEFORE tea finished!</span>
      </div>
      <div>tea: READY!</div>
      <div>toast: READY!</div>
      <div style={{color:T.textMute,marginTop:4}}>breakfast took 3 seconds</div>
    </Output>
    <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,marginBottom:16}}>
      Look at the print order — both chores started immediately. Breakfast in <strong style={{color:T.green}}>3 seconds</strong> instead of 6.
    </p>
    <BreakfastDemo />
    <Callout type="win">
      <strong style={{color:T.green}}>So threads always make things faster… right?</strong>{" "}
      Hold that thought — this is where every beginner (and plenty of professionals) gets fooled.
    </Callout>
  </div>
);}

function S3() { return (
  <div>
    <p style={{fontSize:15,color:T.textSec,lineHeight:1.8,marginBottom:16}}>
      Tea and toast were <em style={{color:T.accent}}>waiting</em> chores. Now try a <em style={{color:T.orange}}>working</em> chore:
      chopping vegetables — in code, real computation, no sleeping:
    </p>
    <CodeBlock code={`def chop():                # no sleep! the CPU is actually working
    n = 60_000_000
    while n > 0:
        n -= 1
    print("done chopping!")`} />
    <p style={{fontSize:13.5,color:T.textSec,lineHeight:1.7,marginBottom:14}}>
      Give it to <strong style={{color:T.accent}}>two threads</strong>, and to <strong style={{color:T.orange}}>two processes</strong> — the code is <em>identical</em> except for one word:
    </p>
    <CodeBlock code={`# two THREADS chopping
import threading
a = threading.Thread(target=chop)
b = threading.Thread(target=chop)
a.start(); b.start()
a.join();  b.join()`} label="python — threading" />
    <CodeBlock code={`# two PROCESSES chopping (identical pattern, one word changes)
import multiprocessing
a = multiprocessing.Process(target=chop)
b = multiprocessing.Process(target=chop)
a.start(); b.start()
a.join();  b.join()`} label="python — multiprocessing" />
    <Output>
      <div>
        2 THREADS chopping:{"   "}
        <span style={{color:T.yellow}}>3.0 seconds</span>
        <span style={{color:T.textMute,marginLeft:12}}>&lt;- threads didn't help?!</span>
      </div>
      <div>
        2 PROCESSES chopping:{"  "}
        <span style={{color:T.green}}>1.8 seconds</span>
        <span style={{color:T.textMute,marginLeft:12}}>&lt;- processes did!</span>
      </div>
    </Output>
    <p style={{fontSize:15,color:T.textSec,lineHeight:1.8}}>
      <strong style={{color:T.red}}>Surprise.</strong> For chopping, threads gained nothing. Why?
    </p>
  </div>
);}

function S4() { return (
  <div>
    <p style={{fontSize:15,color:T.textSec,lineHeight:1.8,marginBottom:16}}>
      Python's kitchen has a strange rule, called the <strong style={{color:T.yellow}}>Global Interpreter Lock</strong>:
      there is only <em style={{color:T.accent}}>one chef's hat</em>, and only the helper wearing it may actually <em>work</em>.
      Helpers pass the hat back and forth very quickly, so it <em>looks</em> like teamwork — but at any
      instant, only one is truly working.
    </p>
    <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
      padding:"16px 20px",marginBottom:18,textAlign:"center"}}>
      <div style={{fontSize:40,marginBottom:12}}>🎩</div>
      <div style={{fontSize:13.5,color:T.textSec,lineHeight:1.8}}>
        One hat. One kitchen. Many helpers — but only the one wearing the hat can run Python code right now.
      </div>
    </div>
    <Callout type="default">
      <strong style={{color:T.orange}}>A helper who is <em>waiting</em> takes the hat off.</strong>{" "}
      Waiting for a kettle, a toaster, a download, a database — none of that needs the hat.
      That's why tea + toast worked beautifully with threads.
      But <em>chopping</em> needs the hat every single moment — so two chopping threads just take turns, gaining nothing.
    </Callout>
    <p style={{fontSize:14,color:T.textSec,lineHeight:1.8,marginBottom:16}}>
      <strong style={{color:T.orange}}>Multiprocessing</strong> fixes it the blunt way: build a <em style={{color:T.accent}}>second kitchen</em>, with its own hat, own fridge, own helper.
      Two kitchens really do chop simultaneously.
    </p>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
      <div style={{background:"rgba(56,189,248,0.06)",border:`1px solid ${T.accent}33`,borderRadius:10,padding:"16px"}}>
        <div style={{fontSize:11,fontWeight:700,color:T.accent,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10}}>🧵 Threading</div>
        {["One shared kitchen","One GIL (chef's hat)","Helpers take turns with the hat","Great for: waiting (I/O)","Low startup cost","Shared memory — easy communication"].map((x,i)=>(
          <div key={i} style={{display:"flex",gap:8,marginBottom:6,fontSize:12.5,color:T.textSec}}>
            <span style={{color:T.accent,minWidth:16}}>›</span><span>{x}</span>
          </div>
        ))}
      </div>
      <div style={{background:"rgba(251,146,60,0.06)",border:`1px solid ${T.orange}33`,borderRadius:10,padding:"16px"}}>
        <div style={{fontSize:11,fontWeight:700,color:T.orange,letterSpacing:1.5,textTransform:"uppercase",marginBottom:10}}>⚙️ Multiprocessing</div>
        {["Separate kitchen per process","Own GIL, own memory","Truly parallel on multiple CPUs","Great for: working (CPU-bound)","Higher startup cost","Must pass data via Queue/Pipe"].map((x,i)=>(
          <div key={i} style={{display:"flex",gap:8,marginBottom:6,fontSize:12.5,color:T.textSec}}>
            <span style={{color:T.orange,minWidth:16}}>›</span><span>{x}</span>
          </div>
        ))}
      </div>
    </div>
    <Callout type="question">
      The cost of multiprocessing: kitchens are expensive to build (processes take time and memory to start),
      and they don't share a fridge — ingredients must be passed through a window (a <code style={{color:T.accent,fontFamily:"monospace"}}>Queue</code>), which is slower than sharing memory like threads do.
    </Callout>
  </div>
);}

function S5() { return (
  <div>
    <TTable
      headers={["Your chore is…","Example","Use","Why"]}
      rows={[
        [<span style={{color:T.accent}}>Waiting (I/O-bound)</span>,
          "downloads, APIs, databases, files, web scraping",
          <span style={{color:T.accent}}>threading</span>,
          "Waiting helpers drop the hat — cheap and fast"],
        [<span style={{color:T.orange}}>Working (CPU-bound)</span>,
          "math, image processing, ML training, data crunching",
          <span style={{color:T.orange}}>multiprocessing</span>,
          "Each kitchen gets its own hat — real parallelism"],
      ]}
    />
    <div style={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:12,
      padding:"20px 24px",marginBottom:20,textAlign:"center",lineHeight:1.8}}>
      <div style={{fontSize:17,color:T.textPri,marginBottom:8}}>
        <strong style={{color:T.accent}}>Threads</strong> share one kitchen and one hat —
        great for <strong style={{color:T.accent}}>waiting</strong>.
      </div>
      <div style={{fontSize:17,color:T.textPri}}>
        <strong style={{color:T.orange}}>Processes</strong> get their own kitchens —
        great for <strong style={{color:T.orange}}>working</strong>.
      </div>
    </div>
    <CodeBlock code={`# Quick decision guide
import time, threading, multiprocessing

def is_io_bound():
    time.sleep(1)          # any kind of waiting
    # fetch URL, read file, query DB...

def is_cpu_bound():
    n = 50_000_000
    while n > 0: n -= 1   # actual computation

# I/O-bound → threads (cheap, share memory, GIL released during wait)
threads = [threading.Thread(target=is_io_bound) for _ in range(5)]

# CPU-bound → processes (each gets own GIL and Python interpreter)
processes = [multiprocessing.Process(target=is_cpu_bound) for _ in range(5)]

# Same pattern either way:
for t in threads:  t.start()
for t in threads:  t.join()

for p in processes: p.start()
for p in processes: p.join()`} />
    <Callout type="win">
      <strong style={{color:T.green}}>One-liner test:</strong>{" "}
      If your function calls <code style={{color:T.accent,fontFamily:"monospace"}}>sleep()</code>, opens a file,
      makes a request, or queries a database — it's I/O-bound → use <strong>threading</strong>.
      If it just computes (loops, maths, transforms) — it's CPU-bound → use <strong>multiprocessing</strong>.
    </Callout>
    <p style={{fontSize:14,color:T.textMute,fontStyle:"italic",marginTop:8,textAlign:"center"}}>
      Next time someone mentions the GIL at a meetup, just think:{" "}
      <em style={{color:T.textSec}}>one chef's hat — and waiting doesn't need the hat.</em>
    </p>
  </div>
);}

// ─── SECTIONS METADATA ────────────────────────────────────────────────────────
const SECTIONS = [
  {id:"home", icon:"🏠", title:"Overview",                  sub:"Breakfast analogy + what you'll learn"},
  {id:"s1",   icon:"🤖", title:"1. The Robot (Sequential)", sub:"One thing at a time — 6 seconds"},
  {id:"s2",   icon:"🧵", title:"2. Threading",              sub:"Start both, then wait — 3 seconds + live demo"},
  {id:"s3",   icon:"🥕", title:"3. The Chopping Surprise",  sub:"CPU-bound: threads don't help"},
  {id:"s4",   icon:"🎩", title:"4. The GIL Explained",      sub:"One chef's hat, two kitchens"},
  {id:"s5",   icon:"🗺", title:"5. The Takeaway",           sub:"Decision guide + portable pattern"},
];

const sectionComponents = {s1:S1,s2:S2,s3:S3,s4:S4,s5:S5};

function HomePage({ onNavigate }) {
  return (
    <div>
      <div style={{marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:2,textTransform:"uppercase",marginBottom:8}}>
          PYTHON · CONCURRENCY FOR HUMANS
        </div>
        <h1 style={{fontSize:24,fontWeight:700,color:T.textPri,margin:"0 0 10px",lineHeight:1.35}}>
          Threads vs Processes,<br/>
          <span style={{color:T.accent}}>Explained with Breakfast</span>
        </h1>
        <p style={{fontSize:14,color:T.textSec,lineHeight:1.7,maxWidth:540,margin:0}}>
          If you can make tea and toast, you can understand the GIL. Every Python beginner eventually
          meets <em style={{color:T.accent}}>threading</em>, <em style={{color:T.orange}}>multiprocessing</em>,
          and one villain: the <em style={{color:T.yellow}}>GIL</em>. Most explanations start with jargon.
          We'll start with breakfast.
        </p>
      </div>
      <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(56,189,248,0.06)",
          border:`1px solid ${T.accent}33`,borderRadius:20,padding:"4px 14px"}}>
          <span style={{fontSize:14}}>🧵</span>
          <span style={{fontSize:12,color:T.accent,fontWeight:600}}>threading — I/O-bound</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(251,146,60,0.06)",
          border:`1px solid ${T.orange}33`,borderRadius:20,padding:"4px 14px"}}>
          <span style={{fontSize:14}}>⚙️</span>
          <span style={{fontSize:12,color:T.orange,fontWeight:600}}>multiprocessing — CPU-bound</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,background:"rgba(252,211,77,0.06)",
          border:`1px solid ${T.yellow}33`,borderRadius:20,padding:"4px 14px"}}>
          <span style={{fontSize:14}}>🎩</span>
          <span style={{fontSize:12,color:T.yellow,fontWeight:600}}>GIL — the one hat</span>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        {SECTIONS.filter(s=>s.id!=="home").map((s,i)=>(
          <button key={s.id} onClick={()=>onNavigate(s.id)}
            style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:10,
              padding:"12px 14px",cursor:"pointer",textAlign:"left",fontFamily:"inherit",
              display:"flex",gap:10,alignItems:"flex-start"}}>
            <span style={{fontSize:18,lineHeight:1.2,minWidth:24}}>{s.icon}</span>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:T.textPri,marginBottom:2}}>{s.title}</div>
              <div style={{fontSize:11,color:T.textSec}}>{s.sub}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PythonThreadingVsMultiprocessing() {
  const [current, setCurrent] = useState("home");
  const [visited, setVisited] = useState(new Set(["home"]));
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const contentRef = useRef(null);

  const navigate = (id) => {
    setCurrent(id);
    setVisited(prev => new Set([...prev, id]));
    if (contentRef.current) contentRef.current.scrollTop = 0;
  };

  const currentIdx = SECTIONS.findIndex(s => s.id === current);
  const prevSection = currentIdx > 0 ? SECTIONS[currentIdx - 1] : null;
  const nextSection = currentIdx < SECTIONS.length - 1 ? SECTIONS[currentIdx + 1] : null;
  const CurrentComponent = sectionComponents[current];
  const progressPct = Math.round((visited.size / SECTIONS.length) * 100);

  return (
    <div style={{display:"flex",flexDirection:"column",height:"calc(100vh - 70px)",
      background:T.bg,color:T.textPri,fontFamily:"system-ui,-apple-system,sans-serif",overflow:"hidden"}}>

      {/* TOP BAR */}
      <div style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",
        background:T.surface,borderBottom:`1px solid ${T.border}`,flexShrink:0}}>
        <button onClick={()=>setSidebarOpen(o=>!o)}
          style={{background:"none",border:"none",color:T.textSec,cursor:"pointer",fontSize:18,padding:"2px 4px",lineHeight:1}}>☰</button>
        <span style={{fontSize:15,fontWeight:600,color:T.textPri}}>🧵⚙️ Threading vs Multiprocessing</span>
        <div style={{flex:1,height:4,background:T.card,borderRadius:2,overflow:"hidden"}}>
          <div style={{width:`${progressPct}%`,height:"100%",
            background:`linear-gradient(90deg,${T.accent},${T.orange})`,
            borderRadius:2,transition:"width .4s ease"}} />
        </div>
        <span style={{fontSize:12,color:T.accent,minWidth:36,textAlign:"right",fontWeight:600}}>{progressPct}%</span>
      </div>

      <div style={{display:"flex",flex:1,overflow:"hidden"}}>
        {/* SIDEBAR */}
        {sidebarOpen && (
          <div style={{width:220,minWidth:220,background:T.surface,borderRight:`1px solid ${T.border}`,overflowY:"auto",flexShrink:0}}>
            <div style={{padding:"8px 6px"}}>
              {SECTIONS.map(s=>(
                <button key={s.id} onClick={()=>navigate(s.id)}
                  style={{display:"flex",alignItems:"center",gap:8,width:"100%",
                    padding:"7px 10px",border:"none",borderRadius:7,cursor:"pointer",
                    background:current===s.id?"rgba(56,189,248,0.08)":"none",fontFamily:"inherit",marginBottom:2,
                    borderLeft:current===s.id?`2px solid ${T.accent}`:"2px solid transparent"}}>
                  <span style={{fontSize:13,minWidth:20}}>{s.icon}</span>
                  <div style={{flex:1,textAlign:"left"}}>
                    <div style={{fontSize:11.5,
                      color:current===s.id?T.accent:visited.has(s.id)?T.green:T.textSec,
                      fontWeight:current===s.id?600:400,lineHeight:1.35}}>
                      {s.title}
                    </div>
                    <div style={{fontSize:10,color:T.textMute,marginTop:1,lineHeight:1.3}}>{s.sub}</div>
                  </div>
                  {visited.has(s.id) && current!==s.id &&
                    <span style={{fontSize:10,color:T.green}}>✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* MAIN CONTENT */}
        <div ref={contentRef} style={{flex:1,overflowY:"auto"}}>
          <div style={{maxWidth:860,margin:"0 auto",padding:"24px 28px"}}>
            {current==="home" ? (
              <HomePage onNavigate={navigate} />
            ) : (
              <div>
                <div style={{marginBottom:22}}>
                  <div style={{fontSize:11,fontWeight:600,color:T.accent,letterSpacing:1.5,
                    textTransform:"uppercase",marginBottom:8}}>
                    Section {currentIdx} of {SECTIONS.length-1}
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                    <span style={{fontSize:22}}>{SECTIONS[currentIdx].icon}</span>
                    <h1 style={{fontSize:21,fontWeight:700,color:T.textPri,margin:0}}>{SECTIONS[currentIdx].title}</h1>
                  </div>
                  <p style={{fontSize:13,color:T.textSec,margin:0}}>{SECTIONS[currentIdx].sub}</p>
                </div>
                {CurrentComponent && <CurrentComponent />}
                <div style={{display:"flex",justifyContent:"space-between",marginTop:28,
                  paddingTop:18,borderTop:`1px solid ${T.border}`}}>
                  {prevSection ? (
                    <button onClick={()=>navigate(prevSection.id)}
                      style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:9,
                        padding:"9px 16px",cursor:"pointer",color:T.textSec,fontFamily:"inherit",fontSize:13}}>
                      ← {prevSection.icon} {prevSection.title}
                    </button>
                  ) : <div/>}
                  {nextSection && (
                    <button onClick={()=>navigate(nextSection.id)}
                      style={{background:"rgba(56,189,248,0.08)",border:`1px solid ${T.accent}44`,
                        borderRadius:9,padding:"9px 16px",cursor:"pointer",color:T.accent,
                        fontFamily:"inherit",fontSize:13,fontWeight:600}}>
                      {nextSection.icon} {nextSection.title} →
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
